import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import './UserManagementPopover.css';

function UserManagementPopover({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    email: '',
    username: '',
    full_name: '',
    password: '',
    role: 'user',
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.detail || 'Felhasználók betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDelete = async (userId) => {
    if (!window.confirm('Biztosan törölni szeretné a felhasználót?')) return;
    try {
      setDeletingId(userId);
      await api.delete(`/api/admin/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.detail || 'Felhasználó törlése sikertelen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError('');
      const payload = {
        email: form.email,
        username: form.username,
        full_name: form.full_name,
        password: form.password,
        role: form.role,
      };
      const response = await api.post('/api/admin/users', payload);
      setUsers((prev) => [...prev, response.data]);
      setForm({ email: '', username: '', full_name: '', password: '', role: 'user' });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err.response?.data?.detail || 'Felhasználó létrehozása sikertelen');
    } finally {
      setCreating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="user-mgmt-overlay">
      <div className="user-mgmt-popover">
        <div className="user-mgmt-header">
          <h2>Felhasználókezelés</h2>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {error && <div className="user-mgmt-error">{error}</div>}

        <div className="user-mgmt-toolbar">
          <button
            className="add-user-button"
            type="button"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Új felhasználó
          </button>
        </div>

        {showAddForm && (
          <div className="user-mgmt-add-form-wrapper">
            <form className="user-mgmt-add-form" onSubmit={handleCreate}>
              <div className="form-row">
                <label>E-mail cím</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  disabled={creating}
                />
              </div>
              <div className="form-row">
                <label>Felhasználónév</label>
                <input
                  type="text"
                  name="username"
                  required
                  value={form.username}
                  onChange={handleChange}
                  disabled={creating}
                />
              </div>
              <div className="form-row">
                <label>Teljes név</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={form.full_name}
                  onChange={handleChange}
                  disabled={creating}
                />
              </div>
              <div className="form-row">
                <label>Jelszó</label>
                <input
                  type="password"
                  name="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  disabled={creating}
                />
              </div>
              <div className="form-row">
                <label>Szerepkör</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  disabled={creating}
                >
                  <option value="admin">admin</option>
                  <option value="user">user</option>
                </select>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowAddForm(false)}
                  disabled={creating}
                >
                  Mégse
                </button>
                <button type="submit" className="primary-button" disabled={creating}>
                  {creating ? 'Létrehozás...' : 'Létrehozás'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="user-mgmt-list">
          {loading ? (
            <div className="user-mgmt-loading">Betöltés...</div>
          ) : users.length === 0 ? (
            <div className="user-mgmt-empty">Még nincsenek felhasználók.</div>
          ) : (
            users.map((u) => (
              <div key={u.id} className="user-mgmt-item">
                <div className="user-mgmt-info">
                  <div className="user-mgmt-name">{u.full_name}</div>
                  <div className="user-mgmt-meta">
                    <span>{u.username}</span>
                    <span>{u.email}</span>
                    <span className="user-mgmt-role">{u.role}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="icon-button danger"
                  onClick={() => handleDelete(u.id)}
                  disabled={deletingId === u.id}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagementPopover;
